import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { useNavigate } from "@tanstack/react-router"
import { ArrowRight, ArrowUpRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useAuthStore } from "@/store/auth.store"
import { toast } from "sonner"

interface StepData {
  id: number
  stepNumber: string
  title: string
  description: string
  image: string
}

const STEPS: StepData[] = [
  {
    id: 1,
    stepNumber: "01",
    title: "Verify Facility & Capacity",
    description: "Submit GST/IEC and plant audit credentials to earn your Audited Indian Manufacturer verification badge.",
    image: "/step1.png",
  },
  {
    id: 2,
    stepNumber: "02",
    title: "Pick & Launch Products",
    description: "Choose trending items and publish custom B2B production quotes to global enterprise buyers with one click.",
    image: "/step2.png",
  },
  {
    id: 3,
    stepNumber: "03",
    title: "Milestone Escrow Payouts",
    description: "Ship orders with 100% capital protection. Guaranteed payments release automatically upon inspection milestone sign-offs.",
    image: "/step3.png",
  },
]

export function SupplierSteps({ className }: { className?: string } = {}) {
  const [activeStep, setActiveStep] = useState(1)
  const [isHovered, setIsHovered] = useState(false)
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const role = useAuthStore((s) => s.user?.role)?.toUpperCase()

  useEffect(() => {
    if (isHovered) return

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev % STEPS.length) + 1)
    }, 1500)

    return () => clearInterval(interval)
  }, [isHovered])

  return (
    <section
      id="supplier-steps"
      className={cn(
        "pb-20 md:pb-24 max-w-7xl xl:max-w-[clamp(1280px,92vw,1440px)] 2xl:max-w-[clamp(1280px,92vw,1560px)] mx-auto px-4 sm:px-6 lg:px-8 bg-white select-none overflow-hidden scroll-mt-12 md:scroll-mt-14",
        className
      )}
    >
      {/* Header */}
      <div className="text-center max-w-4xl mx-auto mb-6 sm:mb-8 flex flex-col items-center gap-4">
        <h2 className="text-2xl font-sans sm:text-3xl md:text-[34px] lg:text-[40px] font-bold text-slate-950 tracking-tight leading-tight">
          <span className="whitespace-nowrap">Start Selling in</span>{" "}
          <span className="text-primary font-bold whitespace-nowrap">Just a Few Steps.</span>
        </h2>
        <Button
          type="button"
          onClick={() => {
            if (isAuthenticated) {
              if (role === "SELLER") {
                navigate({ to: "/products" })
              } else {
                const roleLabel = role === "ADMIN" ? "an Admin" : "a Buyer"
                toast.warning(`You are logged in as ${roleLabel} and cannot access seller pages.`, {
                  id: "seller-access-restricted",
                })
              }
            } else {
              navigate({ to: "/signup", search: { role: "seller" } })
            }
          }}
          className="relative text-xs sm:text-[13px] font-bold rounded-full h-10 p-1 ps-4.5 pe-11 group transition-all duration-500 hover:ps-11 hover:pe-4.5 w-fit overflow-hidden cursor-pointer bg-primary text-primary-foreground hover:bg-primary-hover shadow-md shadow-primary/25 hover:shadow-lg hover:shadow-primary/30 active:scale-95"
        >
          <span className="relative z-10 transition-all duration-500">
            Start Selling
          </span>
          <div className="absolute right-1 w-8 h-8 bg-white text-slate-900 rounded-full flex items-center justify-center transition-all duration-500 group-hover:right-[calc(100%-36px)] group-hover:rotate-45 shadow-xs">
            <ArrowUpRight size={14} />
          </div>
        </Button>
      </div>

      {/* 3D Overlapping Interactive Card Deck */}
      <div className="relative flex items-center justify-center min-h-[250px] md:h-[330px] max-w-5xl mx-auto px-2">
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0 md:-space-x-6 lg:-space-x-8 w-full md:h-full">
          {STEPS.map((step) => {
            const isActive = activeStep === step.id

            return (
              <motion.div
                key={step.id}
                onClick={() => setActiveStep(step.id)}
                onMouseEnter={() => {
                  setActiveStep(step.id)
                  setIsHovered(true)
                }}
                onMouseLeave={() => setIsHovered(false)}
                layout
                transition={{ type: "spring", stiffness: 280, damping: 28 }}
                className={`relative rounded-3xl overflow-hidden cursor-pointer group transition-all duration-500 w-full md:w-[240px] lg:w-[270px] shrink-0 ${
                  isActive
                    ? "md:scale-105 z-20 shadow-2xl shadow-slate-950/25 ring-1 ring-primary/30 h-[250px] sm:h-[275px] md:h-[300px]"
                    : "md:scale-95 z-10 opacity-75 hover:opacity-95 grayscale-[40%] hover:grayscale-0 shadow-xl h-[195px] sm:h-[220px] md:h-[240px]"
                }`}
              >
                {/* Background Image */}
                <img
                  src={step.image}
                  alt={step.title}
                  className={`w-full h-full object-cover transition-transform duration-700 ease-out ${
                    isActive ? "scale-100 group-hover:scale-105" : "scale-100"
                  }`}
                />

                {/* Vignette Overlay */}
                <div
                  className={`absolute inset-0 transition-opacity duration-300 ${
                    isActive
                      ? "bg-gradient-to-t from-black/85 via-black/35 to-black/10"
                      : "bg-black/40 hover:bg-black/30"
                  }`}
                />

                {/* Watermark Typographic Number (Center for Inactive, Top-Left for Active) */}
                <div
                  className={`absolute pointer-events-none transition-all duration-500 ${
                    isActive
                      ? "top-3 sm:top-4 left-4 sm:left-5"
                      : "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  }`}
                >
                  <span
                    className={`font-extralight text-4xl sm:text-5xl md:text-6xl tracking-tight leading-none select-none transition-all duration-300 drop-shadow-[0_2px_10px_rgba(0,0,0,0.4)] ${
                      isActive ? "text-white/95" : "text-white/85"
                    }`}
                  >
                    {step.stepNumber}
                  </span>
                </div>

                {/* Content Overlay — Rendered ONLY on Active Card */}
                {isActive && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    className="absolute bottom-0 inset-x-0 p-4 sm:p-5 flex flex-col justify-end text-left z-10"
                  >
                    <h3 className="text-sm sm:text-[15px] md:text-base font-bold text-white tracking-tight leading-snug">
                      {step.title}
                    </h3>

                    <p className="mt-1 text-[10.5px] sm:text-[11.5px] text-white/85 leading-relaxed line-clamp-2">
                      {step.description}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            )
          })}
        </div>
      </div>


    </section>
  )
}
